import React from 'react';
import { AdminSettings, ModuleID, Role, PermissionSet } from '../../types';

type PermissionsTabProps = {
  roles: Role[];
  modules: ModuleID[];
  settings: AdminSettings;
  updatePermission: (role: Role, module: ModuleID, type: keyof PermissionSet, value: boolean) => void;
  toggleAllForRole: (role: Role, enabled: boolean) => void;
  saveSettings: (s: AdminSettings) => void;
  isSaving: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
};

const ACTIONS: Array<keyof PermissionSet> = ['view', 'create', 'edit', 'delete', 'approve'];

const MODULE_LABELS: Partial<Record<ModuleID, string>> = {
  DASHBOARD: 'Command Center',
  OPERATIONS_TV: 'Operations TV',
  QUOTER: 'Charter',
  SCHEDULED: 'Scheduled',
  PLANNER: 'Planner',
  FRMS: 'FRMS',
  FLEET: 'Fleet',
  FUEL: 'Fuel',
  CUSTOMERS: 'Customers',
  CONTACTS: 'Contacts',
  FINANCE: 'Finance',
  ADMIN: 'System Registry',
  ADMIN_TOOLS: 'Admin Center',
  USERS: 'Users',
  PILOTS: 'Pilot Registry',
  AUDIT: 'Audit',
  STRATEGY: 'Strategy',
  OUTBOUND: 'Outbound',
  INBOX: 'Inbound Hub',
  COMMS: 'Comms Dashboard',
};

const roleLabel = (role: Role) => role.replace(/_/g, ' ');
const moduleLabel = (module: ModuleID) => MODULE_LABELS[module] || module.replace(/_/g, ' ');

export const PermissionsTab: React.FC<PermissionsTabProps> = ({
  roles,
  modules,
  settings,
  updatePermission,
  toggleAllForRole,
  saveSettings,
  isSaving,
  saveStatus,
}) => {
  const hasAllPermissionsEnabled = (perms?: Partial<PermissionSet>) =>
    !!perms &&
    perms.view === true &&
    perms.create === true &&
    perms.edit === true &&
    perms.delete === true &&
    perms.approve === true;

  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
      <div className="flex flex-col gap-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-indigo-50/40 px-8 py-7 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-2xl font-black tracking-tight text-slate-900">Permissions Matrix</h3>
          <p className="mt-1 text-sm font-medium text-slate-600">Manage what each role can see and do in every module.</p>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus === 'saving' && (
            <span className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-amber-700">Saving...</span>
          )}
          {saveStatus === 'saved' && (
            <span className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-emerald-700">Saved</span>
          )}
          {saveStatus === 'error' && (
            <span className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-rose-700">Save Failed</span>
          )}
          <button
            onClick={() => saveSettings(settings)}
            disabled={isSaving}
            className="rounded-xl bg-slate-900 px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save Permission Changes
          </button>
        </div>
      </div>

      <div className="border-b border-slate-100 bg-slate-50/60 px-8 py-3 text-xs font-semibold text-slate-500">
        Tip: Use column actions for module-wide changes and row actions for role-wide changes.
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1500px] border-collapse text-left">
          <thead className="sticky top-0 z-20 bg-slate-100/95 backdrop-blur">
            <tr>
              <th className="sticky left-0 z-30 border-r border-slate-300 bg-slate-100 px-6 py-5 text-xs font-black uppercase tracking-[0.18em] text-slate-700">
                Role
              </th>
              {modules.map((module) => {
                const allEnabledForModule = roles.every((role) => hasAllPermissionsEnabled(settings.permissions[role]?.[module]));
                return (
                  <th key={module} className="border-r border-slate-200 px-4 py-4 text-center align-top">
                    <div className="space-y-2">
                      <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-700">{moduleLabel(module)}</p>
                      <button
                        type="button"
                        onClick={() => {
                          roles.forEach((role) => {
                            ACTIONS.forEach((action) => updatePermission(role, module, action, !allEnabledForModule));
                          });
                        }}
                        className={`rounded-lg border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em] transition ${
                          allEnabledForModule
                            ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        }`}
                        title={allEnabledForModule ? 'Revoke all permissions in this module for all roles' : 'Grant all permissions in this module for all roles'}
                      >
                        {allEnabledForModule ? 'Revoke All' : 'Grant All'}
                      </button>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {roles.map((role, rowIndex) => {
              const allPermissionsForRole = modules.every((module) => hasAllPermissionsEnabled(settings.permissions[role]?.[module]));

              return (
                <tr key={role} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                  <td className="sticky left-0 z-10 border-r border-slate-300 bg-inherit px-6 py-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-lg font-black text-slate-800">{roleLabel(role)}</p>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Role ID: {role}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleAllForRole(role, !allPermissionsForRole)}
                        className={`rounded-lg border px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em] transition ${
                          allPermissionsForRole
                            ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        }`}
                        title={allPermissionsForRole ? 'Revoke all permissions for this role' : 'Grant all permissions for this role'}
                      >
                        {allPermissionsForRole ? 'Revoke Role' : 'Grant Role'}
                      </button>
                    </div>
                  </td>

                  {modules.map((module) => {
                    const perms = settings.permissions[role]?.[module] || ({} as PermissionSet);
                    return (
                      <td key={`${role}-${module}`} className="border-r border-slate-100 px-4 py-4">
                        <div className="grid grid-cols-1 gap-2">
                          {ACTIONS.map((action) => (
                            <label
                              key={action}
                              className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500"
                            >
                              <input
                                type="checkbox"
                                checked={perms[action] || false}
                                onChange={(e) => updatePermission(role, module, action, e.target.checked)}
                                className="h-4 w-4 rounded border border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500"
                              />
                              {action}
                            </label>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
